import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { LinkItem } from '@atlaskit/menu';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { SPACE_SETTINGS_LANDING_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { SpaceWatchButton } from '@confluence/action-buttons/entry-points/spaceWatch.ts';
import { SpaceStarMenuItem } from '@confluence/action-buttons/entry-points/spaceStar.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';

import type { SpaceProp } from '../types.ts';

import { MenuButton } from './PresentationalComponents.tsx';

const i18n = defineMessages({
	menu: {
		id: 'space-directory.space.menu',
		defaultMessage: 'More actions',
		description: "Tooltip message on '...' menu button",
	},
	spaceSettings: {
		id: 'space-directory.space.settings.link',
		defaultMessage: 'Space settings',
		description: 'Link to the Settings page for a Confluence Space',
	},
});

const showAtNarrowWidths = css({
	display: 'none',
	'@media only screen and (max-width: 500px)': {
		display: 'contents',
	},
});

const MenuComponent: FC<SpaceProp & WithFlagsProps> = ({ space }) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		setIsOpen((o) => !o);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'more',
				source: 'spaceDirectoryScreen',
				attributes: {
					spaceId: space.id,
				},
			},
		}).fire();
	}, [space.id, createAnalyticsEvent]);

	return (
		// TODO: Add `shouldRenderToParent` to component to improve accessibility. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.atlassian.net/wiki/spaces/DST/pages/5402216605/shouldRenderToParent+prop+usages+in+cc-disco+packages
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => <PopupContent space={space} />}
			trigger={(triggerProps) => (
				<Tooltip content={<FormattedMessage {...i18n.menu} />} hideTooltipOnMouseDown>
					<MenuButton
						{...(triggerProps as any)}
						onClick={onClick}
						isOpen={isOpen}
						aria-label={intl.formatMessage(i18n.menu)}
					>
						<MoreIcon label="" spacing="spacious" />
					</MenuButton>
				</Tooltip>
			)}
			placement="bottom-start"
		/>
	);
};

const PopupContent: FC<SpaceProp> = ({ space }) => {
	const analytics = {
		source: 'spaceDirectoryScreen',
		attributes: {
			spaceId: space.id,
		},
	};

	return (
		<div>
			<LinkItem
				iconBefore={<SettingsIcon spacing="spacious" label="" />}
				href={SPACE_SETTINGS_LANDING_PAGE.toUrl({ spaceKey: space.alias || space.key })}
				isDisabled={!space.currentUser?.isAdmin}
				UNSAFE_shouldDisableRouterLink
			>
				<FormattedMessage {...i18n.spaceSettings} />
			</LinkItem>
			<div css={showAtNarrowWidths}>
				<SpaceWatchButton
					spaceId={space.id!}
					isWatched={!!space.currentUser?.isWatched}
					analytics={analytics}
					inMenu
				/>
				<SpaceStarMenuItem
					spaceId={space.id!}
					spaceKey={space.key!}
					isDisabled={false}
					isStarred={!!space.currentUser?.isFavourited}
					analytics={analytics}
				/>
			</div>
		</div>
	);
};

export const Menu = withFlags(MenuComponent);
